import { TROPHY_TIER_IDS, type TrophyTier } from '@crowdfpl/domain/gamification';
import { type ReactElement } from 'react';
import { TROPHY_TIER_EMOJI } from '@/components/gamification/trophy-copy';
import { trophyTierFillClass } from '@/components/gamification/trophy-tier-styles';
import { cn } from '@/helpers/utils';

type LadderSegmentState = 'locked' | 'unlocked' | 'current' | 'progress' | 'max';

type TrophyProgressLadderProps = {
  highestTierIndex: number | null;
  isMaxed: boolean;
  /** 0–1 fill for the segment toward the next unlocked tier. */
  nextProgress?: number | null;
  /** Compact: thinner bar, no emoji labels / summary (Almost There strip). */
  compact?: boolean;
  className?: string;
};

function formatTierLabel(tier: TrophyTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function clampProgress(progress: number | null | undefined): number {
  if (progress == null || Number.isNaN(progress)) {
    return 0;
  }
  return Math.min(1, Math.max(0, progress));
}

function resolveLadderSegmentState(params: {
  index: number;
  highestTierIndex: number | null;
  isMaxed: boolean;
}): LadderSegmentState {
  const { index, highestTierIndex, isMaxed } = params;

  if (isMaxed) {
    return index === TROPHY_TIER_IDS.length - 1 ? 'max' : 'unlocked';
  }

  if (highestTierIndex == null) {
    return index === 0 ? 'progress' : 'locked';
  }

  if (index < highestTierIndex) {
    return 'unlocked';
  }

  if (index === highestTierIndex) {
    return 'current';
  }

  if (index === highestTierIndex + 1) {
    return 'progress';
  }

  return 'locked';
}

function segmentClassName(tier: TrophyTier, state: LadderSegmentState): string {
  if (state === 'locked' || state === 'progress') {
    return 'bg-foreground/[0.06]';
  }

  const fill = trophyTierFillClass(tier);
  if (state === 'current') {
    return cn(fill, 'ring-2 ring-offset-1 ring-offset-background ring-gamification-rare-ring/50');
  }
  if (state === 'max') {
    return cn(fill, 'ring-2 ring-gamification-rare-ring ring-offset-1 ring-offset-background');
  }
  return fill;
}

/**
 * Six-segment ladder for multi-tier trophies. States come from API highest tier /
 * maxed flag; the next segment optionally fills with `nextProgress`. Emoji labels
 * under segments mirror the Lovable prototype; compact mode hides them.
 */
export function TrophyProgressLadder({
  highestTierIndex,
  isMaxed,
  nextProgress = null,
  compact = false,
  className,
}: TrophyProgressLadderProps): ReactElement {
  const fillPct = Math.round(clampProgress(nextProgress) * 100);
  const segments = TROPHY_TIER_IDS.map((tier, index) => ({
    tier,
    state: resolveLadderSegmentState({ index, highestTierIndex, isMaxed }),
  }));

  const summary =
    highestTierIndex == null
      ? 'No tiers unlocked yet'
      : isMaxed
        ? `Max tier ${formatTierLabel(TROPHY_TIER_IDS[TROPHY_TIER_IDS.length - 1])}`
        : `Highest tier ${formatTierLabel(TROPHY_TIER_IDS[highestTierIndex])}`;

  return (
    <div
      className={cn('space-y-1.5', className)}
      role="img"
      aria-label={`Trophy tier ladder. ${summary}.`}
      data-testid="trophy-progress-ladder"
      data-compact={compact ? 'true' : 'false'}
    >
      <div
        className={cn('flex items-center gap-1', compact ? 'h-1.5' : 'h-2')}
        aria-hidden
        data-gamification-motion="decorative"
      >
        {segments.map(({ tier, state }) =>
          state === 'progress' ? (
            <div
              key={tier}
              data-tier={tier}
              data-state={state}
              data-progress={fillPct}
              className="relative h-full flex-1 overflow-hidden rounded-full bg-foreground/[0.06]"
              title={formatTierLabel(tier)}
            >
              <div
                className={cn('absolute inset-y-0 left-0 rounded-full', trophyTierFillClass(tier))}
                style={{ width: `${fillPct}%` }}
              />
            </div>
          ) : (
            <div
              key={tier}
              data-tier={tier}
              data-state={state}
              className={cn('h-full flex-1 rounded-full', segmentClassName(tier, state))}
              title={formatTierLabel(tier)}
            />
          ),
        )}
      </div>
      {!compact ? (
        <div className="flex justify-between" aria-hidden>
          {segments.map(({ tier, state }, index) => (
            <span
              key={tier}
              className={cn(
                'flex-1 text-center text-[10px] leading-none',
                state === 'locked' && 'opacity-40 grayscale',
                state === 'progress' && 'opacity-70',
              )}
              data-tier-label={tier}
              data-current={index === highestTierIndex ? 'true' : 'false'}
            >
              {TROPHY_TIER_EMOJI[tier]}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
